import { ref, computed, watch, type ComputedRef } from 'vue'
import { toast } from '@rapoo/element'
import {
  AUDIO_RHYTHM_COLORS,
  AUDIO_RHYTHM_MODES,
  DEFAULT_AUDIO_RHYTHM_SETTINGS,
  LIGHTING_TARGET,
  createDeviceLightSink,
  supportsDeviceLightSink,
  type AudioRhythmMode,
  type LightingEffectDefinition,
  type keyMapLighting,
} from '@rapoo/device-sdk'
import { useLocaleStore } from '@/stores'
import { useLoading } from '@/composables/useLoading'
import {
  beginLatest,
  useLightingCapability,
  useLightingLifecycle,
  useLightingSDK,
} from './useLightingSDK'
import { useAudioRhythm } from './useAudioRhythm'
import { AudioCaptureError, AudioCaptureErrorCode } from './audioCapture'

/**
 * 音乐律动（LIGHTING_TARGET.AudioRhythm）业务。
 *
 * 灯效列表来自 manifest。采集 / 引擎 / 设备下发与 sdk-test 共用 useAudioRhythm：
 * 启动只做「要音频 → 开渲染循环」，GAME_MODE=0 / 0x01 / 擦黑由 sink 在首帧推送时完成，
 * 不要在这里提前 setCurrentEffect(0)（测试页也没有这一步）。
 */

export type UseAudioRhythmLightingOptions = {
  autoInit?: boolean
}

function isAudioRhythmMode(value: string | undefined): value is AudioRhythmMode {
  return AUDIO_RHYTHM_MODES.some((item) => item.id === value)
}

export function useAudioRhythmLighting(options: UseAudioRhythmLightingOptions = {}) {
  const { autoInit = true } = options
  const { t } = useLocaleStore()
  const { isLoading, withLoading } = useLoading()
  const { currentDevice, hasAudioRhythm } = useLightingCapability()
  const { getEffects, getLighting } = useLightingSDK()
  // 与测试页一致：显式传入当前设备，保证引擎布局 / sink 与 store 设备同源。
  const {
    mode: engineMode,
    colorId,
    settings,
    status,
    statusMessage,
    usingMic,
    running,
    frameColors,
    setDeviceSink,
    startSystemAudio,
    stopAudio,
  } = useAudioRhythm(currentDevice)

  let lastErrorToastAt = 0

  const resolveCaptureErrorMessage = (err?: unknown, message?: string) => {
    const code = err instanceof AudioCaptureError ? err.code : undefined
    if (code === AudioCaptureErrorCode.NoAudioTrack) {
      return t('lighting.audioRhythmStatus.noAudioTrack')
    }
    const text = err instanceof Error ? err.message : (message ?? '')
    if (text.includes('未获取到音频轨道')) {
      return t('lighting.audioRhythmStatus.noAudioTrack')
    }
    if (text.includes('音频分享已结束')) {
      return t('lighting.audioRhythmStatus.shareEnded')
    }
    return text || t('lighting.audioRhythmStatus.error')
  }

  const notifyCaptureError = (err?: unknown, message?: string) => {
    const now = Date.now()
    if (now - lastErrorToastAt < 500) return
    lastErrorToastAt = now
    toast.error(resolveCaptureErrorMessage(err, message))
  }

  const effects = ref<LightingEffectDefinition[]>([])
  const currentMode = ref(-1)
  const initRequestId = ref(0)

  const currentEffectInfo = computed(() =>
    effects.value.find((e) => e.id === currentMode.value),
  ) as ComputedRef<LightingEffectDefinition | undefined>

  const keyMapRGB = computed<keyMapLighting>(() => {
    const list: keyMapLighting = []
    for (const [index, rgb] of frameColors.value) {
      list.push({ index, rgb: { r: rgb.r, g: rgb.g, b: rgb.b } })
    }
    return list
  })

  const resetUi = () => {
    stopAudio()
    effects.value = []
    currentMode.value = -1
    colorId.value = AUDIO_RHYTHM_COLORS[0]!.id
    settings.value = { ...DEFAULT_AUDIO_RHYTHM_SETTINGS }
  }

  const init = async (opts?: { force?: boolean }) => {
    if (!currentDevice.value || !hasAudioRhythm.value) return

    const task = beginLatest(initRequestId)

    await withLoading(
      async () => {
        const list = getLighting() ? await getEffects(LIGHTING_TARGET.AudioRhythm) : []
        if (!task.alive()) return
        effects.value = list
        if (currentMode.value < 0 && list[0]) {
          currentMode.value = list[0].id
        }
      },
      { skipIfLoading: opts?.force ? false : true },
    )
  }

  const setMode = async (mode?: LightingEffectDefinition) => {
    if (!mode) return
    currentMode.value = mode.id
  }

  const toggleCapture = async () => {
    if (status.value === 'requesting') return
    if (status.value === 'active' || running.value) {
      stopAudio()
      return
    }
    // 与测试页 onShareSystem 一致：只启动采集 + 渲染循环。
    // GAME_MODE=0 / 0x01 / 整盘擦黑全部留给 createDeviceLightSink.ensureCustomMode，
    // 在检测到音效并首次 pushFrame 时紧挨着执行，避免「过早进自定义、灯定义区刷残色」。
    try {
      await startSystemAudio()
    } catch (err) {
      notifyCaptureError(err)
    }
  }

  watch(status, (next) => {
    if (next !== 'error') return
    notifyCaptureError(undefined, statusMessage.value)
    stopAudio()
  })

  watch(
    currentEffectInfo,
    (info) => {
      const modeKey = info?.modeKey
      if (isAudioRhythmMode(modeKey)) {
        engineMode.value = modeKey
      }
    },
    { immediate: true },
  )

  watch(
    currentDevice,
    (device) => {
      if (!device) {
        setDeviceSink(null)
        stopAudio()
        return
      }
      // 音乐律动 0xdd 下发仅 GCOME_AP 实现；非支持设备或未声明 audio-rhythm 时不创建 sink
      if (!hasAudioRhythm.value || !supportsDeviceLightSink(device)) {
        setDeviceSink(null)
        return
      }
      setDeviceSink(createDeviceLightSink(device))
    },
    { immediate: true },
  )

  useLightingLifecycle({
    autoInit,
    reset: resetUi,
    init,
    onInvalidate: () => {
      initRequestId.value++
    },
  })

  return {
    isLoading,
    hasAudioRhythm,
    effects,
    currentMode,
    currentEffectInfo,
    colorId,
    settings,
    keyMapRGB,
    captureStatus: status,
    captureUsingMic: usingMic,
    captureRunning: running,
    init,
    setMode,
    toggleCapture,
    stopCapture: stopAudio,
  }
}
